import { FC } from 'react';

import { Formik, type FormikHelpers } from 'formik';
import useModal from 'ui/partials/Modal/useModal';
import c from 'utils/constants';
import { dayjs, payloadDayjs } from 'utils/providers/Timezone';
import { useTimezone } from 'utils/providers/timezone.provider';

import EventForm from '../forms/EventForm';
import { EVENT_SCHEMA, EVENT_FORM } from '../forms/schema';
import { useCreateEvent } from '../hooks';
import getNextTime from '../utils/getNextTime';

interface PROPS {
  dealId?: string;
  params?: Record<string, any>;
  initialStart?: Date;
  initialEnd?: Date;
}

const CreateEventModal: FC<PROPS> = ({
  dealId,
  params,
  initialStart,
  initialEnd,
}) => {
  const { hideModal } = useModal();
  const { timezone } = useTimezone({ source: 'local' });
  const { mutate: createEvent, isLoading: createLoading } = useCreateEvent({
    dealId,
    ...params,
  });

  const INIT_FORM: EVENT_FORM = {
    title: '',
    date: initialStart
      ? dayjs(initialStart).tz(timezone).toDate()
      : dayjs().toDate(),
    startAt: initialStart
      ? dayjs(initialStart).tz(timezone).format('HH:mm')
      : getNextTime(),
    endAt: initialEnd
      ? dayjs(initialEnd).tz(timezone).format('HH:mm')
      : getNextTime(15),
    content: '',
    guests: [],
    eventUrl: null,
    notifyBefore: 10,
    type: 'VIDEO',
  };

  const onSubmit = (
    { date, ...rest }: EVENT_FORM,
    { setSubmitting }: FormikHelpers<EVENT_FORM>,
  ) => {
    const d = dayjs(date).format('YYYY-MM-DD');

    const startAt = payloadDayjs(
      `${d} ${rest.startAt}`,
      `${c.DATE_FORMAT} HH:mm`,
      timezone,
    );

    const endAt = payloadDayjs(
      `${d} ${rest.endAt}`,
      `${c.DATE_FORMAT} HH:mm`,
      timezone,
    );

    const payload = {
      ...rest,
      dealId,
      startAt,
      endAt,
    };

    createEvent(payload as any, {
      onSuccess: () => {
        hideModal();
      },
      onSettled: () => {
        setSubmitting(false);
      },
    });
  };

  return (
    <div className="flex flex-col">
      <Formik
        initialValues={INIT_FORM}
        validationSchema={EVENT_SCHEMA}
        onSubmit={onSubmit}
      >
        {() => <EventForm isLoading={createLoading} />}
      </Formik>
    </div>
  );
};

export default CreateEventModal;
